// Shared display helpers for the Call Experience tab.
import { createContext, useContext } from 'react';

/**
 * Page-wide view preferences: `technical` shows internal keys, columns and option names; `hear` shows
 * every line with its placeholders filled in, as a caller would hear it.
 */
export const CxPrefs = createContext({ technical: false, hear: false });
export const useCxPrefs = () => useContext(CxPrefs);

/**
 * Plain-language wording for conditions, where the API label reads awkwardly as a phrase.
 * `yes` is used for "key" (or "key=yes"), `no` for "!key".
 */
const CONDITION_PHRASES = {
  'patient.thirtyDayPayor': { yes: "the patient's insurance pays 30 days at a time", no: "the patient's insurance isn't a 30-day payer" },
  'patient.insuranceAllows90': { yes: "the patient's insurance allows a 90-day supply", no: "the patient's insurance doesn't allow a 90-day supply" },
  'patient.insuranceAsksAutoship': { yes: "one of the patient's insurances asks about autoship", no: "none of the patient's insurances asks about autoship" },
  'patient.insuranceReadsEducation': { yes: "the patient's primary insurance reads the education statement", no: "the patient's primary insurance doesn't read the education statement" },
  'patient.mobileNumber': { yes: "the patient's number is a mobile", no: "the patient's number isn't a mobile" },
  'patient.hasDobOnFile': { yes: 'a birth year is on file', no: 'no birth year is on file' },
  'patient.hasEligibleItems': { yes: 'the patient has eligible supplies', no: 'nothing is eligible' },
  'patient.completeAddress': { yes: 'a complete address is on file', no: 'no complete address is on file' },
  'patient.prefersSpanish': { yes: 'the patient prefers Spanish', no: 'the patient prefers English' },
  'company.repTransferAvailable': { yes: 'the company offers a representative', no: "the company doesn't offer a representative" },
  'call.withinCompanyHours': { yes: "it's within the company's hours", no: "it's outside the company's hours" },
  'call.fromVoicemail': { yes: 'the patient came back from the voicemail', no: "the patient didn't come back from the voicemail" },
  'call.hasPreSupplyQuestions': { yes: 'the company has questions before the supplies', no: 'the company has no questions before the supplies' },
  'call.anyAccepted': { yes: 'at least one item was chosen', no: 'nothing was chosen' },
};

const VALUE_WORDS = { machine: 'an answering machine', human: 'a person', fax: 'a fax', unknown: 'unknown', afterHours: 'after hours' };

const lowerFirst = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

/** One guard term ("key", "!key" or "key=value") in words. */
function describeTerm(term, labels) {
  const negated = term.startsWith('!');
  const [key, value] = term.replace(/^!/, '').split('=');
  const phrase = CONDITION_PHRASES[key];
  const label = labels[key];
  if (value !== undefined && value !== 'yes' && value !== 'no') {
    const said = `${lowerFirst(label || key)} is ${VALUE_WORDS[value] || value}`;
    return negated ? `not (${said})` : said;
  }
  const yes = negated === (value === 'no');
  if (phrase) return yes ? phrase.yes : phrase.no;
  if (!label) return negated ? `not ${key}` : key;
  return yes ? lowerFirst(label) : `not: ${lowerFirst(label)}`;
}

/**
 * A guard ("a&!b&c=x") as a sentence fragment, e.g. "the patient's number is a mobile and the company
 * offers a representative". `conditions` is the journey's condition list (key + label).
 */
export function describeGuard(guard, conditions) {
  if (!guard) return '';
  const labels = Object.fromEntries((conditions || []).map((c) => [c.key, c.label]));
  return guard.split('&').map((t) => describeTerm(t.trim(), labels)).join(' and ');
}

/** Sample values used to preview "{Placeholder}" text. */
export const SAMPLE_VALUES = {
  CompanyName: 'Acme Medical',
  HolidayName: 'Thanksgiving',
  CompanyPhoneNumber: '800-555-0100',
  CallbackCode: '48213',
  LastDayToOrder: 'December 20, 2026',
  StartDay: 'Monday',
  EndDay: 'Friday',
  StartTime: '9 AM',
  EndTime: '5 PM',
  CoachName: 'Sam',
  PatientName: 'Maria Lopez',
  Name: 'Maria Lopez',
  Supplies: 'CPAP',
  Items: 'a mask and standard tubing',
  AttemptsLeft: 'You have 2 attempts remaining.',
  0: 'a mask',
  1: '1',
};

/** Fills "{Name}" placeholders with sample values (the company's name when one is being viewed). */
export function fillSample(text, companyName) {
  const values = companyName ? { ...SAMPLE_VALUES, CompanyName: companyName } : SAMPLE_VALUES;
  return (text || '').replace(/\{(\w+)\}/g, (m, name) => values[name] ?? m);
}

/**
 * Placeholder values for showing a journey's lines as heard: the sample values, the company's name, and the
 * product line's supplies. `{Items}` is every item the line can offer (or the two-item example in the order
 * readback); a patient preview passes `items` to use that patient's list.
 */
export function heardValues(journey, { companyName, items } = {}) {
  const supplies = journey?.supplies;
  const values = { ...SAMPLE_VALUES, ...(companyName ? { CompanyName: companyName } : {}) };
  if (!supplies) return { base: values, readback: values };
  const base = { ...values, Supplies: supplies.suppliesPhrase, Items: items ?? supplies.offerableItemsSpoken };
  return { base, readback: { ...base, Items: supplies.readbackExampleSpoken } };
}

/** Text with its placeholders filled; filled values are underlined with a tooltip saying where they came from. */
export function HeardText({ text, values, source = 'Example value' }) {
  const parts = (text || '').split(/(\{\w+\})/g);
  return parts.map((part, i) => {
    const name = /^\{(\w+)\}$/.exec(part)?.[1];
    if (!name) return part;
    if (values[name] == null) return <span key={i} className="cx-ph">{part}</span>;
    return <span key={i} className="cx-filled" title={`${source} for {${name}}`}>{values[name]}</span>;
  });
}

/** Renders text with its "{Placeholder}" tokens highlighted. */
export function Highlighted({ text }) {
  const parts = (text || '').split(/(\{\w+\})/g);
  return parts.map((part, i) =>
    /^\{\w+\}$/.test(part) ? <span key={i} className="cx-ph">{part}</span> : part);
}

const ORIGINS = {
  company: { cls: 'cx-badge-co', label: 'custom', title: 'Own wording for this company or group' },
  parent: { cls: 'cx-badge-par', label: 'via group', title: 'Inherited from the parent group' },
  global: { cls: 'cx-badge-gl', label: 'default', title: 'Global default in the database' },
  fallback: { cls: 'cx-badge-gl', label: 'code default', title: 'No wording in the database; the built-in fallback is spoken' },
  builtin: { cls: 'cx-badge-bi', label: 'built-in', title: 'Built into the code; the same for every company' },
};

export function OriginBadge({ origin }) {
  const o = ORIGINS[origin] || ORIGINS.global;
  return <span className={`cx-badge ${o.cls}`} title={o.title}>{o.label}</span>;
}

/** Reads text aloud with the browser's voice, as a stand-in for Google TTS. */
export function speak(text, lang) {
  if (!('speechSynthesis' in window)) return false;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = lang === 'es' ? 'es-US' : 'en-US';
  window.speechSynthesis.speak(utterance);
  return true;
}

const KEY_WORDS = {
  zero: '0', one: '1', two: '2', three: '3', four: '4', five: '5', six: '6', seven: '7', eight: '8', nine: '9',
  cero: '0', uno: '1', dos: '2', tres: '3', cuatro: '4', cinco: '5', seis: '6', siete: '7', ocho: '8', nueve: '9',
  pound: '#', star: '*',
};
const PRESS = /\b(press|presione|pulse|marque|oprima)\b/i;
const KEY_TOKEN = /(?:^|[^\w{])([0-9#*])(?![\w}])|\b(zero|one|two|three|four|five|six|seven|eight|nine|cero|uno|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|pound|star)\b/gi;

/**
 * Keys a line tells the caller to press: every single digit or number word (English or Spanish) in a
 * sentence that says "press" / "presione". "Press 1 for yes, or 2 for no." gives 1 and 2.
 */
export function mentionedKeys(text) {
  const keys = new Set();
  (text || '').split(/(?<=[.!?])\s+/).forEach((sentence) => {
    if (!PRESS.test(sentence)) return;
    for (const m of sentence.matchAll(KEY_TOKEN)) keys.add(m[1] ?? KEY_WORDS[m[2].toLowerCase()]);
  });
  return [...keys];
}

/** The keys callers can press where a line is used (union across usages); null when any key is accepted or none apply. */
export function acceptedKeys(usage) {
  const keys = new Set((usage || []).flatMap((u) => u.keys || []));
  if (keys.size === 0 || keys.has('any')) return null;
  return [...keys].sort();
}

export const journeyKey = (j) => `${j.id}:${j.productLineId ?? ''}`;

export const JOURNEY_ICONS = { inbound: '📥', 'outbound-coach': '📤', 'resupply-ivr': '🤖' };
